import React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { Box, Collapse, Stack, Typography, Alert } from "@mui/material";
import styled from "styled-components";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    border-radius: 6px;
`;

const TooltipLine = styled.p`
    font-size: 12px;
    margin: 2px 0;
`;

const IssueRow = styled.div`
    font-size: 11px;
    padding: 3px 0;
    border-bottom: 1px solid rgba(88, 88, 88, 0.3);
    &:last-child {
        border-bottom: none;
    }
`;

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <TooltipContainer>
            <TooltipLine style={{ fontWeight: 600, marginBottom: 4 }}>{label}</TooltipLine>
            {payload.map((p) => (
                <TooltipLine key={p.name} style={{ color: p.fill }}>
                    {p.name}: {Number(p.value).toFixed(1)}h
                </TooltipLine>
            ))}
        </TooltipContainer>
    );
};

const SprintTimeAnalysis = ({ fetchAction, hasSprintSelection }) => {
    const [expanded, setExpanded] = React.useState(null);

    const raw = fetchAction?.data?.timeAnalysis;

    if (!hasSprintSelection) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info" sx={{ fontSize: 13 }}>
                    Select one or more sprints to view time analysis.
                </Alert>
            </Box>
        );
    }

    if (!raw || raw.length === 0) return <NoDataPlaceholder variant="list" errorMessage={null} />;

    const chartData = raw.map((row) => ({
        name: row.assignee_name || "Unassigned",
        Estimated: Number(row.estimated_hours ?? 0),
        Actual: Number(row.actual_hours ?? 0),
        issues: row.issues ?? [],
    }));

    return (
        <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
            <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 52)}>
                <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
                    onClick={(e) => {
                        if (!e?.activeLabel) return;
                        setExpanded((prev) => (prev === e.activeLabel ? null : e.activeLabel));
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" unit="h" tick={{ fontSize: 11, fill: "#aaa" }} />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={110}
                        tick={{ fontSize: 11, fill: "#ddd" }}
                    />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "rgba(255,255,255,0.05)" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Estimated" fill="#8884d8" maxBarSize={14} radius={[0, 3, 3, 0]} />
                    <Bar dataKey="Actual" fill="#82ca9d" maxBarSize={14} radius={[0, 3, 3, 0]} />
                </BarChart>
            </ResponsiveContainer>

            {chartData.map((row) =>
                expanded === row.name && row.issues?.length ? (
                    <Collapse key={row.name} in timeout="auto">
                        <Box
                            sx={{
                                mx: 2,
                                mb: 1,
                                border: "1px solid #585858",
                                borderRadius: 1,
                                p: 1,
                            }}
                        >
                            <Typography sx={{ fontSize: 11, fontWeight: 600, mb: 0.5 }}>
                                {row.name} — issue breakdown
                            </Typography>
                            {row.issues.map((issue) => (
                                <IssueRow key={issue.key}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography sx={{ fontSize: 11 }}>
                                            <strong>{issue.key}</strong>{" "}
                                            {issue.sprintId && (
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        color: "#888",
                                                        marginRight: 4,
                                                    }}
                                                >
                                                    [Sprint {issue.sprintId}]
                                                </span>
                                            )}
                                            {issue.summary}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ ml: 1, flexShrink: 0 }}
                                        >
                                            {issue.estimatedHours != null && (
                                                <Typography sx={{ fontSize: 10, color: "#8884d8" }}>
                                                    ~{issue.estimatedHours}h est.
                                                </Typography>
                                            )}
                                            <Typography sx={{ fontSize: 10, color: "#82ca9d" }}>
                                                {issue.actualHours}h actual
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </IssueRow>
                            ))}
                        </Box>
                    </Collapse>
                ) : null
            )}
            <Typography
                sx={{ fontSize: 10, color: "text.secondary", textAlign: "center", mt: 0.5 }}
            >
                Click a bar to expand issue details
            </Typography>
        </Box>
    );
};

export default SprintTimeAnalysis;
